/**
 * Pure selection-set transitions for the file browser multi-select, plus the
 * entry-level queries over a selection that the components used to spell out
 * inline.
 *
 * Kept free of React so the marquee / modifier-click / select-all logic can be
 * unit-tested in the node-environment vitest suite (no jsdom). The
 * `useFileSelection` hook is a thin state wrapper over the transitions.
 *
 * Selection is a Set of **entry ids** (`entryIdentity.ts`), not display paths —
 * STD-029 R-2. Keying on the path made two rows at one display path a single
 * selectable item and stranded the set on any rename; both are gone as soon as
 * the ids are what goes in. All functions return a NEW Set and never mutate
 * their input.
 *
 * The entry-level queries at the bottom exist for a testing reason as much as a
 * DRY one: `selectedIds.has(entryId(f))` and `selectedIds.has(f.key)` are both
 * well-typed `boolean`, so a call site that reverts to the path compiles, passes
 * every test, and fails only in the browser. Moving the membership test into
 * this module puts it somewhere a unit test can kill the mutant.
 */
import { FileItem } from './types';
import { entryId } from './entryIdentity';

/** Replace the whole selection with a single entry (plain single-click). */
export function replaceSelection(id: string): Set<string> {
  return new Set([id]);
}

/** Toggle one entry in/out of the selection (Ctrl/Cmd-click). */
export function toggleSelection(selected: ReadonlySet<string>, id: string): Set<string> {
  const next = new Set(selected);
  if (next.has(id)) {
    next.delete(id);
  } else {
    next.add(id);
  }
  return next;
}

/**
 * Select the inclusive range between the anchor and the target over the ordered
 * visible list (Shift-click). If there is no anchor, or either id is missing
 * from the list, fall back to selecting just the target.
 */
export function rangeSelection(
  orderedIds: readonly string[],
  anchorId: string | null,
  targetId: string
): Set<string> {
  if (!anchorId) return new Set([targetId]);
  const anchorIdx = orderedIds.indexOf(anchorId);
  const targetIdx = orderedIds.indexOf(targetId);
  if (anchorIdx === -1 || targetIdx === -1) return new Set([targetId]);
  const [lo, hi] = anchorIdx <= targetIdx ? [anchorIdx, targetIdx] : [targetIdx, anchorIdx];
  return new Set(orderedIds.slice(lo, hi + 1));
}

/** Select every visible entry (Ctrl+A). */
export function selectAll(orderedIds: readonly string[]): Set<string> {
  return new Set(orderedIds);
}

/**
 * The visible entries the selection covers, in visible order (Ctrl+X's targets).
 *
 * Order is the grid's, not the click order, so a bulk action's targets and any
 * list rendered from them cannot reshuffle between renders.
 */
export function selectedEntries(
  visible: readonly FileItem[],
  selectedIds: ReadonlySet<string>
): FileItem[] {
  return selectedEntriesBy(visible, selectedIds, entryId);
}

/**
 * `selectedEntries` for any listing that keys its rows its own way — the Shared
 * view's entries are keyed on `SharedEntry.key`, not on a `FileItem` (#1692).
 * The Files browser's version above is this with `entryId`, so the two cannot
 * disagree about what "selected" means.
 */
export function selectedEntriesBy<T>(
  visible: readonly T[],
  selectedIds: ReadonlySet<string>,
  idOf: (entry: T) => string
): T[] {
  return visible.filter((entry) => selectedIds.has(idOf(entry)));
}

/**
 * Which entries an action invoked on `clicked` should act on — the Explorer
 * rule: act on the whole selection when the clicked entry is part of it,
 * otherwise on the clicked entry alone.
 *
 * One definition, three callers (drag-start, Ctrl-drag bulk move, and
 * `shareTargets`, which adds its own registry-row filter on top). It was written
 * out three times before B-1's fix round, and only the `shareTargets` copy was
 * tested — so the drag-start copy could have keyed on the path indefinitely
 * while the suite stayed green.
 */
export function entriesForAction(
  clicked: FileItem,
  selectedIds: ReadonlySet<string>,
  visible: readonly FileItem[]
): FileItem[] {
  return entriesForActionBy(clicked, selectedIds, visible, entryId);
}

/** `entriesForAction` over any keyed listing — the Shared view's, since #1692. */
export function entriesForActionBy<T>(
  clicked: T,
  selectedIds: ReadonlySet<string>,
  visible: readonly T[],
  idOf: (entry: T) => string
): T[] {
  return selectedIds.has(idOf(clicked)) ? selectedEntriesBy(visible, selectedIds, idOf) : [clicked];
}

/**
 * The selection minus whatever is no longer on screen — or the SAME set when
 * nothing left, so a caller can keep it in state without an update loop.
 *
 * A listing that pages or filters in place (the Shared view) keeps its
 * selection across a refetch; an id that scrolled off the page must not stay
 * selected, or "Download (3 files)" would count a file the user cannot see.
 */
export function pruneSelection(
  selectedIds: ReadonlySet<string>,
  orderedIds: readonly string[]
): ReadonlySet<string> {
  const visible = new Set(orderedIds);
  const kept = [...selectedIds].filter((id) => visible.has(id));
  return kept.length === selectedIds.size ? selectedIds : new Set(kept);
}

/**
 * Everything one grid card needs that depends on identity, resolved once.
 *
 * The card's React key, its dnd-kit draggable/droppable id, its `data-entry-id`
 * and its selected/cut flags are all one decision — "which row is this?" — and
 * that decision belongs in tested code. Before this, each was an `entryId(file)`
 * spelled inside JSX, where no unit test can reach it and `tsc` cannot tell a
 * revert to `file.key` from the real thing. `FileGrid` now renders straight from
 * these models and makes no identity decision of its own.
 */
export interface FileCardModel {
  id: string;
  file: FileItem;
  selected: boolean;
  cut: boolean;
}

export function fileCardModels(
  files: readonly FileItem[],
  selectedIds: ReadonlySet<string>,
  cutIds: ReadonlySet<string>
): FileCardModel[] {
  return files.map((file) => {
    const id = entryId(file);
    return { id, file, selected: selectedIds.has(id), cut: cutIds.has(id) };
  });
}
